/**
 * GIM-5455 (E2-6): Trust good saved addresses; re-check bad ones + resubmit logic.
 *
 * A normalized fingerprint of the address fields that matter for "same vs different"
 * (L1 + L2 + city + state + zip + country) plus a session-scoped memory of the last
 * AVS decision per fingerprint. This drives the resubmit/re-edit rules:
 *   - after SOFT_BLOCK, resubmitting the SAME address re-prompts;
 *   - a CHANGED address (different fingerprint) is validated as new;
 *   - after HARD_BLOCK, every resubmit re-validates;
 *   - editing a field then reverting it (same fingerprint) triggers no re-check.
 *
 * No client decision logic and no client copy catalog — this only decides skip-vs-run
 * and remembers the server's verdict so we don't nag the shopper for the same address.
 */
import getAddressConst from 'constants/Address';

const FIELD_ORDER = ['address1', 'address2', 'city', 'state', 'postalCode', 'country'];

// CLIENT-ONLY INVARIANT: the module-level maps below are mutated (rememberDecision /
// recordSoftBlockShown) and read (shouldValidateAddress / getSoftBlockShownCount) exclusively
// from browser event handlers and post-await AVS callbacks — never during SSR render. Keep it
// that way: writing to them during server render would bleed one shopper's AVS decisions into
// another request in this isomorphic bundle.
// Session-scoped memory: fingerprint -> last server decision. Lives for the page session.
let decisionMemory = Object.create(null);

// Session-scoped count of how many times a SOFT_BLOCK modal was shown per fingerprint.
// GIM-5455 Auriel update 2026-07-01: soft-block re-prompts are capped at once (total 2 modals:
// initial + 1 re-prompt). On the third submission of the same soft-blocked address, proceed.
let softBlockShownCounts = Object.create(null);

// The re-prompt cap: once the SAME fingerprint has shown this many SOFT_BLOCK modals, stop
// re-prompting and proceed. 2 = the initial SOFT_BLOCK modal + exactly one re-prompt.
const SOFT_BLOCK_PROMPT_CAP = 2;

const normalizeField = value =>
    String(value == null ? '' : value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

/**
 * Stable normalized fingerprint string of the address fields used for same-vs-different
 * comparison (the allowed fields joined with '|' — not a cryptographic hash).
 * Case- and whitespace-insensitive so cosmetic edits do not look like a new address.
 * Each field is encodeURIComponent-escaped before joining so a literal '|' typed into any
 * field cannot shift the delimiters and collide two different addresses to one fingerprint.
 */
const addressFingerprint = (address = {}) => FIELD_ORDER.map(field => encodeURIComponent(normalizeField(address[field]))).join('|');

/** Remember the server's decision for an address (keyed by its fingerprint). */
const rememberDecision = (address, decision) => {
    decisionMemory[addressFingerprint(address)] = decision;
};

/** The last remembered decision for an address, or undefined if never seen this session. */
const getLastDecision = address => decisionMemory[addressFingerprint(address)];

/** Clear the session memory (test reset / new session). */
const clearDecisionMemory = () => {
    decisionMemory = Object.create(null);
    softBlockShownCounts = Object.create(null);
};

/**
 * Record that a SOFT_BLOCK modal was shown for this address fingerprint.
 * GIM-5455: re-prompt is capped at once — call this immediately after dispatching the modal.
 */
const recordSoftBlockShown = address => {
    const fp = addressFingerprint(address);
    softBlockShownCounts[fp] = (softBlockShownCounts[fp] || 0) + 1;
};

/** How many times the SOFT_BLOCK modal has been shown for this address fingerprint. */
const getSoftBlockShownCount = address => softBlockShownCounts[addressFingerprint(address)] || 0;

/**
 * The skip-gate. Decide whether the AVS call should run for a saved/selected address.
 *
 *   - addressValidated === true  -> skip (already verified saved address, PRD §11.9 Δ7).
 *   - last decision ACCEPTED / SUGGESTED / BYPASSED for the SAME fingerprint -> skip
 *     (do not nag for an address already resolved this session).
 *   - EVERYTHING ELSE -> run (fail open): never validated this session, SOFT_BLOCK/HARD_BLOCK
 *     (re-prompt/re-validate on resubmit), and any unexpected/null/future decision value.
 *
 * Fail-open is deliberate: the ONLY decisions that suppress AVS are the explicitly-known
 * "resolved" ones. An unknown decision must never silently skip validation.
 */
const shouldValidateAddress = ({ address, addressValidated, isEdited } = {}) => {
    // GIM-5455 Auriel: isEdited=true overrides the skip-gate so edited verified addresses re-validate.
    if (addressValidated === true && !isEdited) {
        return false;
    }

    const { ADDRESS_DECISION } = getAddressConst();
    const lastDecision = getLastDecision(address);

    // Skip ONLY for decisions that resolved the address this session (ACCEPTED / SUGGESTED /
    // BYPASSED); everything else — never-seen, SOFT/HARD_BLOCK, or any unexpected value — fails
    // open and re-runs AVS. Direct comparison keeps this allocation-free on the hot gate path.
    return lastDecision !== ADDRESS_DECISION.ACCEPTED && lastDecision !== ADDRESS_DECISION.SUGGESTED && lastDecision !== ADDRESS_DECISION.BYPASSED;
};

export {
    addressFingerprint,
    rememberDecision,
    getLastDecision,
    clearDecisionMemory,
    shouldValidateAddress,
    recordSoftBlockShown,
    getSoftBlockShownCount,
    SOFT_BLOCK_PROMPT_CAP
};
